import React, { useState, useEffect, useRef } from 'react';

const ImageWithLoader = ({ 
  src, 
  alt, 
  className, 
  priority = false,
  onLoad,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef(null);

  useEffect(() => {
    // If priority image, load immediately
    if (priority) {
      setIsInView(true);
      return;
    }

    // Setup Intersection Observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [priority]);

  useEffect(() => {
    // Check if image is already cached
    if (isInView && src) {
      const img = new Image();
      img.src = src;
      
      if (img.complete) {
        setIsLoaded(true);
        if (onLoad) onLoad();
      } else {
        img.onload = () => {
          setIsLoaded(true);
          if (onLoad) onLoad();
        };
      }
    }
  }, [isInView, src, onLoad]);

  return (
    <div ref={imgRef} className={`relative ${className}`}>
      {/* Placeholder/Skeleton */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-pulse rounded-md" />
      )}
      
      {/* Actual Image */}
      {isInView && (
        <img
          src={src}
          alt={alt}
          className={`${className} transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading={priority ? 'eager' : 'lazy'}
          onLoad={() => {
            setIsLoaded(true);
            if (onLoad) onLoad();
          }}
          {...props}
        />
      )}
    </div>
  );
};

export default ImageWithLoader;
